// AutismAi - Advanced Pre/Post Video Evaluation Workflow
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Plus, X, ArrowRight, Trash2, Upload, Video, Wand2, Camera, FileDown, BarChart3, Save, Eye, Play, Sparkles, Check, AlertTriangle, RefreshCw, Mic, Square, Circle, Target, Pencil, Printer } from "lucide-react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { printPremium, downloadPremium } from "./PremiumDoc";
import { useT } from "./i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL;
const fmtErr = (d) => !d ? "Error" : typeof d === "string" ? d : Array.isArray(d) ? d.map(e=>e?.msg||JSON.stringify(e)).join(" ") : d?.msg || JSON.stringify(d);
const tt = (lang, ar, en) => lang === "en" ? en : ar;
const absUrl = (u) => !u ? "" : u.startsWith("http") ? u : `${BACKEND_BASE}${u}`;
const fmtTime = (s) => {
  if (s == null || isNaN(s)) return "00:00";
  const m = Math.floor(s/60), r = Math.floor(s%60);
  return `${String(m).padStart(2,"0")}:${String(r).padStart(2,"0")}`;
};

const Btn = ({ children, variant="primary", ...p }) => {
  const s = {
    primary:"bg-[#2F5233] hover:bg-[#1E3622] text-white",
    outline:"bg-white hover:bg-[#F0EFEB] text-[#1A201E] border border-[#E5E2DB]",
    ghost:"hover:bg-[#F0EFEB] text-[#4A5553]",
    danger:"bg-[#D9534F] hover:bg-[#B73E3A] text-white",
    accent:"bg-[#769FCD] hover:bg-[#527598] text-white",
  };
  return <button {...p} className={`px-4 py-2 rounded-lg font-medium transition disabled:opacity-50 ${s[variant]} ${p.className||""}`}>{children}</button>;
};
const Input = (p) => <input {...p} className={`w-full px-3 py-2 rounded-lg bg-white border border-[#E5E2DB] focus:ring-2 focus:ring-[#2F5233]/30 focus:border-[#2F5233] outline-none ${p.className||""}`}/>;
const Card = ({ children, className="", ...rest }) => <div className={`bg-white rounded-xl border border-[#E5E2DB] p-5 shadow-sm ${className}`} {...rest}>{children}</div>;
const Select = (p) => <select {...p} className={`w-full px-3 py-2 rounded-lg bg-white border border-[#E5E2DB] focus:ring-2 focus:ring-[#2F5233]/30 outline-none ${p.className||""}`}>{p.children}</select>;
const Pill = ({ children, color="emerald" }) => {
  const c = {
    emerald:"bg-emerald-100 text-emerald-700",
    blue:"bg-[#769FCD]/15 text-[#527598]",
    amber:"bg-amber-100 text-amber-700",
    rose:"bg-rose-100 text-rose-700",
    slate:"bg-[#F0EFEB] text-[#4A5553]",
    purple:"bg-purple-100 text-purple-700",
  };
  return <span className={`text-[11px] px-2 py-0.5 rounded-full ${c[color]}`}>{children}</span>;
};

// ============= LIST inside a case =============
export function EvaluationsList({ cid }) {
  const { lang } = useT();
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ domains:[], domain_labels:{}, tags:[], tag_labels:{}, evidence_types:[], evidence_labels:{} });
  const [planGoals, setPlanGoals] = useState([]); // [{id, title, domain}]
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ title:"", domain:"", goal_id:"" });
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const load = useCallback(() => {
    axios.get(`${API}/cases/${cid}/evaluations`).then(r => setItems(r.data));
    axios.get(`${API}/cases/${cid}`).then(r => {
      const all = [];
      Object.entries(r.data.plan || {}).forEach(([dom, goals]) => {
        (goals || []).forEach(g => { if (g.id && g.title) all.push({ id:g.id, title:g.title, domain: dom }); });
      });
      setPlanGoals(all);
    });
  }, [cid]);
  useEffect(() => {
    load();
    axios.get(`${API}/evaluations/meta`).then(r => setMeta(r.data));
  }, [load]);

  const create = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = { title: form.title, domain: form.domain };
      if (form.goal_id) payload.goal_id = form.goal_id;
      const { data } = await axios.post(`${API}/cases/${cid}/evaluations`, payload);
      nav(`/cases/${cid}/evaluations/${data.id}`);
    } catch (err) { alert(fmtErr(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const del = async (eid) => {
    if (!window.confirm(tt(lang, "حذف هذا التقييم؟", "Delete this evaluation?"))) return;
    try { await axios.delete(`${API}/cases/${cid}/evaluations/${eid}`); load(); }
    catch (err) { alert(fmtErr(err.response?.data?.detail)); }
  };

  const goalById = (id) => planGoals.find(g => g.id === id);
  const filteredGoals = form.domain ? planGoals.filter(g => g.domain === form.domain) : planGoals;

  return <div className="space-y-5" data-testid="evals-list">
    <Card>
      <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-semibold font-heading flex items-center gap-2"><Video size={20}/>{tt(lang, "التقييم بالفيديو قبل/بعد", "Pre/Post Video Evaluation")}</h2>
          <p className="text-xs text-[#4A5553] mt-1">{tt(lang, "قارن أداء الطفل بين فيديو قياسي قبلي وبعدي بأدلة موثّقة على الخط الزمني، مع اقتراحات ذكية وتقرير مقارنة. اربط كل تقييم بهدف من خطة التدخل لتتبّع التحقق.", "Compare the child's performance between a baseline and post video with timeline-documented evidence, AI suggestions and a comparison report. Link each evaluation to a goal in the intervention plan to track achievement.")}</p>
        </div>
        <Btn onClick={()=>setShow(true)} data-testid="new-eval-btn"><Plus size={14} className="inline ms-1"/>{tt(lang, "تقييم جديد", "New evaluation")}</Btn>
      </div>
      {show && <form onSubmit={create} className="mt-3 p-4 bg-[#F9F8F6] rounded-lg grid md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="eval-form">
        <Input placeholder={tt(lang, "عنوان التقييم (مثال: مهارة طلب المساعدة)", "Evaluation title (e.g. Asking for help)")} required value={form.title} onChange={e=>setForm({...form, title:e.target.value})} data-testid="eval-title"/>
        <Select required value={form.domain} onChange={e=>setForm({...form, domain:e.target.value, goal_id:""})} data-testid="eval-domain">
          <option value="">{tt(lang, "المجال المستهدف", "Target domain")}</option>
          {meta.domains.map(d => <option key={d} value={d}>{meta.domain_labels[d] || d}</option>)}
        </Select>
        <Select value={form.goal_id} onChange={e=>setForm({...form, goal_id:e.target.value})} data-testid="eval-goal">
          <option value="">{tt(lang, "— بدون ربط بهدف —", "— No goal link —")}</option>
          {filteredGoals.map(g => <option key={g.id} value={g.id}>{g.title}</option>)}
        </Select>
        <div className="flex gap-2"><Btn type="submit" disabled={busy} data-testid="eval-create"><Save size={14} className="inline ms-1"/>{busy?"...":tt(lang, "إنشاء", "Create")}</Btn>
          <Btn variant="outline" type="button" onClick={()=>setShow(false)}>{tt(lang, "إلغاء", "Cancel")}</Btn></div>
      </form>}
    </Card>

    {items.length === 0 ? <Card><p className="text-center text-[#4A5553] py-6 text-sm">{tt(lang, "لا توجد تقييمات بعد. ابدأ بإنشاء تقييم جديد لتوثيق فيديو قبلي وبعدي.", "No evaluations yet. Start by creating a new evaluation to document a pre/post video.")}</p></Card> :
      <div className="grid md:grid-cols-2 gap-4">{items.map(e => {
        const baseCount = e.baseline?.notes?.length || 0;
        const finalCount = e.final?.notes?.length || 0;
        const linkedGoal = e.goal_id ? goalById(e.goal_id) : null;
        return <Card key={e.id} className="hover:border-[#2F5233] transition" data-testid={`eval-card-${e.id}`}>
          <div className="flex justify-between items-start gap-2">
            <Link to={`/cases/${cid}/evaluations/${e.id}`} className="flex-1 min-w-0">
              <h3 className="font-semibold truncate">{e.title}</h3>
              <p className="text-xs text-[#4A5553] mt-1">{meta.domain_labels[e.domain] || e.domain}</p>
              {linkedGoal && <p className="text-xs text-[#527598] mt-1 flex items-center gap-1"><Target size={11}/>{tt(lang, "هدف مرتبط", "Linked goal")}: {linkedGoal.title}</p>}
              <div className="flex flex-wrap gap-1 mt-2">
                <Pill color="slate">{tt(lang, "قبلي", "Pre")}: {baseCount} {tt(lang, "ملاحظة", "note(s)")}</Pill>
                <Pill color="slate">{tt(lang, "بعدي", "Post")}: {finalCount} {tt(lang, "ملاحظة", "note(s)")}</Pill>
                {e.comparison_summary ? <Pill color="emerald">{tt(lang, "تقرير جاهز", "Report ready")}</Pill> : <Pill color="amber">{tt(lang, "قيد الإعداد", "In progress")}</Pill>}
              </div>
            </Link>
            <button onClick={()=>del(e.id)} className="p-2 hover:bg-red-50 rounded text-[#D9534F]" data-testid={`eval-del-${e.id}`}><Trash2 size={14}/></button>
          </div>
        </Card>;
      })}</div>}
  </div>;
}

// ============= PHASE GATE RAIL (Clinical 3-Phase Workflow v3.1) =============
function PhaseGateRail({ data, onComparison, comparing }) {
  const baselineSigned = !!data.baseline?.signed_off;
  const finalSigned = !!data.final?.signed_off;
  const hasReport = !!data.comparison_summary;

  // Per-phase progress (0..100)
  const phase1Progress = (() => {
    let p = 0;
    if (data.baseline?.video_url) p += 35;
    if ((data.baseline?.notes||[]).length >= 1) p += 25;
    if ((data.baseline?.notes||[]).length >= 3) p += 10;
    const rb = (data.rubric_scores||{}).baseline || {};
    if (Object.values(rb).some(v => typeof v === "number" && v > 0)) p += 20;
    if (baselineSigned) p = 100;
    return Math.min(p, baselineSigned ? 100 : 99);
  })();
  const phase2Progress = (() => {
    let p = 0;
    if (data.final?.video_url) p += 35;
    if ((data.final?.notes||[]).length >= 1) p += 25;
    if ((data.final?.notes||[]).length >= 3) p += 10;
    const rf = (data.rubric_scores||{}).final || {};
    if (Object.values(rf).some(v => typeof v === "number" && v > 0)) p += 20;
    if (finalSigned) p = 100;
    return Math.min(p, finalSigned ? 100 : 99);
  })();
  const phase3Progress = hasReport ? 100 : 0;

  const phases = [
    { idx: 1, key: "baseline", title: "التقييم القبلي", subtitle: "Baseline · قبل التدخل",
      status: baselineSigned ? "done" : "active", progress: phase1Progress,
      icon: baselineSigned ? <Check size={14}/> : <Circle size={14}/>, color: "#2F5233" },
    { idx: 2, key: "final", title: "التقييم البعدي", subtitle: "Post · بعد التدخل",
      status: finalSigned ? "done" : "active", progress: phase2Progress,
      icon: finalSigned ? <Check size={14}/> : <Circle size={14}/>, color: "#769FCD" },
    { idx: 3, key: "comparison", title: "التحليل والمقارنة", subtitle: "AI · حساب الأثر والثقة",
      status: hasReport ? "done" : "active", progress: phase3Progress,
      icon: hasReport ? <Check size={14}/> : <Sparkles size={14}/>, color: "#E8A317" },
  ];

  const metrics = data.comparison_summary?.metrics;

  return <div className="rounded-2xl border-2 border-[#E5E2DB] bg-white p-4 shadow-sm" data-testid="phase-gate-rail">
    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
      <h2 className="text-base font-semibold font-heading flex items-center gap-2"><Target size={18} className="text-[#2F5233]"/>التسلسل الإكلينيكي للتقييم — المراحل الثلاث</h2>
      <p className="text-[10px] text-[#4A5553]">كل مرحلة تُقفل المرحلة التالية إلى أن تُكتمل وتُوقَّع</p>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      {phases.map((p, i) => {
        const isDone = p.status === "done";
        const isActive = p.status === "active";
        const isLocked = p.status === "locked";
        return <div key={p.key} className={`relative rounded-xl border-2 p-3 transition ${
          isDone ? "border-emerald-300 bg-emerald-50/40" : isActive ? "border-[#2F5233] bg-[#F9F8F6]" : "border-[#E5E2DB] bg-[#F9F8F6]/40 opacity-70"
        }`} data-testid={`phase-${p.idx}`}>
          {i < phases.length - 1 && <div className="hidden md:block absolute top-1/2 -start-2 w-3 h-0.5 bg-[#E5E2DB]"/>}
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs ${
              isDone ? "bg-emerald-600" : isActive ? "" : "bg-gray-300"
            }`} style={isActive && !isDone ? { background: p.color } : {}}>
              {isDone ? <Check size={14}/> : isLocked ? "🔒" : p.idx}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm leading-tight">المرحلة {p.idx}: {p.title}</p>
              <p className="text-[10px] text-[#4A5553]">{p.subtitle}</p>
            </div>
          </div>
          <div className="h-1.5 bg-[#F0EFEB] rounded-full overflow-hidden">
            <div className="h-full transition-all rounded-full" style={{
              width: `${p.progress}%`,
              background: isDone ? "linear-gradient(90deg,#10b981,#34d399)" : `linear-gradient(90deg,${p.color},${p.color}aa)`
            }}/>
          </div>
          <p className="text-[10px] text-[#4A5553] mt-1.5 text-center">{p.progress}%</p>
          {/* Phase 3 inline action */}
          {p.idx === 3 && isActive && !isDone && <Btn variant="accent" onClick={onComparison} disabled={comparing} className="w-full mt-2" data-testid="rail-run-comparison">
            <Sparkles size={12} className="inline ms-1"/>{comparing?"...":"تشغيل التحليل الذكي"}
          </Btn>}
        </div>;
      })}
    </div>
    {/* Effect Size + Confidence panels when Phase 3 done */}
    {metrics && <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-2" data-testid="phase3-metrics">
      <div className="rounded-lg border border-[#E5E2DB] bg-white p-2.5">
        <p className="text-[10px] text-[#4A5553]">حجم الأثر (Effect Size · Cohen's d-like)</p>
        <p className="text-xl font-bold mt-1">{metrics.effect_size_d > 0 ? "+" : ""}{metrics.effect_size_d}</p>
        <p className="text-[10px] text-[#2F5233] font-semibold">{metrics.effect_label_ar}</p>
      </div>
      <div className="rounded-lg border border-[#E5E2DB] bg-white p-2.5">
        <p className="text-[10px] text-[#4A5553]">درجة الثقة</p>
        <p className="text-xl font-bold mt-1">{metrics.confidence_score}%</p>
        <p className={`text-[10px] font-semibold ${metrics.confidence_score>=75?"text-emerald-600":metrics.confidence_score>=50?"text-amber-600":"text-rose-600"}`}>ثقة {metrics.confidence_band}</p>
      </div>
      <div className="rounded-lg border border-[#E5E2DB] bg-white p-2.5">
        <p className="text-[10px] text-[#4A5553]">متوسط Rubric</p>
        <p className="text-sm mt-1"><span className="font-bold text-[#2F5233]">{metrics.baseline_mean}</span> ← <span className="font-bold text-[#527598]">{metrics.final_mean}</span></p>
        <p className="text-[10px] text-[#4A5553]">{metrics.indicators_count} مؤشّر</p>
      </div>
      <div className="rounded-lg border border-[#E5E2DB] bg-white p-2.5">
        <p className="text-[10px] text-[#4A5553]">عدد الأدلة (قبلي/بعدي)</p>
        <p className="text-sm mt-1"><span className="font-bold text-[#2F5233]">{metrics.evidence_count_baseline}</span> · <span className="font-bold text-[#527598]">{metrics.evidence_count_final}</span></p>
        <p className="text-[10px] text-[#4A5553]">ملاحظات موثّقة</p>
      </div>
    </div>}
  </div>;
}

// ============= DETAIL VIEW =============
export function EvaluationDetail() {
  const { cid, eid } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [caseData, setCaseData] = useState(null);
  const [meta, setMeta] = useState({ domains:[], domain_labels:{}, tags:[], tag_labels:{}, evidence_types:[], evidence_labels:{} });
  const [side, setSide] = useState("baseline"); // active side for adding notes
  const [showReport, setShowReport] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [error, setError] = useState("");

  const baseRef = useRef(null);
  const finalRef = useRef(null);

  const load = useCallback(() => {
    axios.get(`${API}/cases/${cid}/evaluations/${eid}`)
      .then(r => setData(r.data))
      .catch(err => setError(fmtErr(err.response?.data?.detail)));
    axios.get(`${API}/cases/${cid}`).then(r => setCaseData(r.data)).catch(() => {});
  }, [cid, eid]);
  useEffect(() => {
    load();
    axios.get(`${API}/evaluations/meta`).then(r => setMeta(r.data));
  }, [load]);

  if (error) return <div className="p-6"><Card><p className="text-[#D9534F]">{error}</p>
    <Link to={`/cases/${cid}`} className="text-sm text-[#2F5233] mt-2 inline-flex items-center gap-1"><ArrowRight size={14}/>العودة للحالة</Link></Card></div>;
  if (!data) return <div className="p-6 text-[#4A5553]">جاري التحميل...</div>;

  // collect plan goals for selector
  const planGoals = [];
  Object.entries(caseData?.plan || {}).forEach(([dom, goals]) => {
    (goals || []).forEach(g => { if (g.id && g.title) planGoals.push({ id:g.id, title:g.title, domain: dom, responsible: g.responsible || "" }); });
  });
  const linkedGoal = data.goal_id ? planGoals.find(g => g.id === data.goal_id) : null;

  // compute this evaluation's own achievement score (Rubric-based)
  const evalScore = (() => {
    const scores = data.rubric_scores || {};
    const inds = Object.keys(scores);
    if (!inds.length) return null;
    let b = 0, f = 0;
    inds.forEach(k => { b += (scores[k]?.baseline || 0); f += (scores[k]?.final || 0); });
    const max = inds.length * 5;
    if (max <= 0) return null;
    return Math.max(0, Math.min(100, Math.round(((f - b) / max) * 100)));
  })();

  const runComparison = async () => {
    setComparing(true);
    try {
      await axios.post(`${API}/cases/${cid}/evaluations/${eid}/comparison`);
      await load();
      setShowReport(true);
    } catch (err) { alert(fmtErr(err.response?.data?.detail)); } finally { setComparing(false); }
  };

  const buildReportOpts = () => {
    const c = data.comparison_summary || {};
    const baseCount = data.baseline?.notes?.length || 0;
    const finalCount = data.final?.notes?.length || 0;
    const list = (arr) => (arr || []).map(x => `<li>${x}</li>`).join("");
    const sec = (t, items) => `<div class="section"><h3>${t}</h3>${(items||[]).length?`<ul>${list(items)}</ul>`:`<p style="color:#6b6b6b">—</p>`}</div>`;
    // Tag distribution
    const tagMap = {};
    (data.baseline?.notes||[]).forEach(n=>{ tagMap[n.tag]=tagMap[n.tag]||{b:0,f:0}; tagMap[n.tag].b++; });
    (data.final?.notes||[]).forEach(n=>{ tagMap[n.tag]=tagMap[n.tag]||{b:0,f:0}; tagMap[n.tag].f++; });
    const tagsHTML = Object.entries(tagMap).length ? `<table class="report"><thead><tr><th>الوسم</th><th>قبلي</th><th>بعدي</th><th>الفرق</th></tr></thead><tbody>${Object.entries(tagMap).map(([t,v])=>{
      const lbl = meta.tag_labels[t] || t;
      const diff = v.f - v.b;
      const pill = diff>0?"pill-emerald":diff<0?"pill-rose":"pill-blue";
      return `<tr><td>${lbl}</td><td>${v.b}</td><td>${v.f}</td><td><span class="pill ${pill}">${diff>0?`+${diff}`:diff}</span></td></tr>`;
    }).join("")}</tbody></table>` : "";
    const body = `
      <div class="section">
        <h3>المؤشرات الرئيسية</h3>
        <div class="grid-3">
          <div class="info-row" style="flex-direction:column"><span class="label">عدد الأدلة - قبلي</span><span class="value" style="font-size:28px;color:#2F5233;font-weight:800">${baseCount}</span></div>
          <div class="info-row" style="flex-direction:column"><span class="label">عدد الأدلة - بعدي</span><span class="value" style="font-size:28px;color:#527598;font-weight:800">${finalCount}</span></div>
          <div class="info-row" style="flex-direction:column"><span class="label">المجال</span><span class="value">${meta.domain_labels[data.domain] || data.domain}</span></div>
        </div>
      </div>
      ${c.overall_interpretation ? `<div class="section"><h3>التفسير العام</h3><div class="quote">${c.overall_interpretation}</div></div>` : ""}
      <div class="grid-2">
        ${sec("لحظات بارزة — قبلي", c.notable_baseline_moments)}
        ${sec("لحظات بارزة — بعدي", c.notable_final_moments)}
      </div>
      <div class="grid-2">
        ${sec("أبرز التحسينات", c.strongest_improvements)}
        ${sec("تحديات متبقية", c.remaining_challenges)}
      </div>
      ${tagsHTML ? `<div class="section"><h3>توزيع الأدلة حسب الوسوم</h3>${tagsHTML}</div>` : ""}
      ${c.professional_caveat ? `<div class="quote">${c.professional_caveat}</div>` : ""}
      <p style="font-size:10px;color:#6b6b6b;text-align:center">تقرير ملاحظي مبني على أدلة فيديو موثّقة — ليس تشخيصاً طبياً</p>
    `;
    return {
      title: "تقرير المقارنة قبل / بعد",
      subtitle: "Pre/Post Video Evaluation Report",
      docType: "تقرير تقييمي رسمي",
      subjectName: data.title,
      subjectMeta: { "المجال": meta.domain_labels[data.domain] || data.domain, "أُنشئ بواسطة": data.created_by || "—" },
      body,
      signatures: [{ role: "المعالج المسؤول" }, { role: "المنسق المعتمد" }, { role: "ولي الأمر / المتلقي" }],
      refPrefix: "EVR",
    };
  };
  const exportReportPDF = () => downloadPremium(buildReportOpts(), `Evaluation-Report-${(data.title||"eval").slice(0,40)}.pdf`);
  const printReport = () => printPremium(buildReportOpts());

  const deleteEval = async () => {
    if (!window.confirm("حذف هذا التقييم نهائياً مع جميع ملاحظاته؟")) return;
    try { await axios.delete(`${API}/cases/${cid}/evaluations/${eid}`); nav(`/cases/${cid}`); }
    catch (err) { alert(fmtErr(err.response?.data?.detail)); }
  };

  const domainLabel = meta.domain_labels[data.domain] || data.domain;

  return <div className="space-y-5 fade-up" data-testid="eval-detail">
    <div className="flex justify-between items-start flex-wrap gap-3">
      <div className="min-w-0 flex-1">
        <Link to={`/cases/${cid}`} className="text-sm text-[#4A5553] hover:text-[#2F5233] inline-flex items-center gap-1"><ArrowRight size={14}/>العودة للحالة</Link>
        <h1 className="text-3xl font-bold font-heading mt-2 flex items-center gap-2"><Video size={26}/>{data.title}</h1>
        <p className="text-sm text-[#4A5553] mt-1">المجال: <strong>{domainLabel}</strong> · أُنشئ بواسطة: {data.created_by || "—"}</p>
        {linkedGoal && <p className="text-xs mt-1 text-[#527598] flex items-center gap-1 flex-wrap">
          <Target size={12}/>هدف مرتبط: <strong className="text-[#1A201E]">{linkedGoal.title}</strong>
          {linkedGoal.responsible && <span className="text-[#4A5553]">· المسؤول: {linkedGoal.responsible}</span>}
        </p>}
        {evalScore != null && <div className="mt-2 flex items-center gap-2" data-testid="eval-score">
          <span className="text-xs text-[#4A5553]">درجة تحقق هذا التقييم:</span>
          <div className="flex items-center gap-2">
            <div className="w-32 h-1.5 bg-[#F0EFEB] rounded-full overflow-hidden">
              <div className={`h-full transition-all ${evalScore>=70?"bg-emerald-500":evalScore>=40?"bg-amber-500":"bg-rose-500"}`} style={{ width: `${Math.max(2, evalScore)}%` }}/>
            </div>
            <span className={`text-xs font-mono px-1.5 py-0.5 rounded ${evalScore>=70?"bg-emerald-100 text-emerald-700":evalScore>=40?"bg-amber-100 text-amber-700":"bg-rose-100 text-rose-700"}`}>{evalScore}%</span>
          </div>
        </div>}
      </div>
      <div className="flex gap-2 flex-wrap">
        <Btn variant="outline" onClick={()=>setShowEdit(true)} data-testid="edit-eval-btn"><Pencil size={14} className="inline ms-1"/>تعديل</Btn>
        <Btn variant="accent" onClick={runComparison} disabled={comparing} data-testid="run-compare-btn">
          <Sparkles size={14} className="inline ms-1"/>{comparing?"جاري التحليل...":(data.comparison_summary?"إعادة إصدار التقرير":"إصدار تقرير المقارنة")}
        </Btn>
        {data.comparison_summary && <>
          <Btn variant="outline" onClick={()=>setShowReport(s=>!s)} data-testid="toggle-report-btn"><Eye size={14} className="inline ms-1"/>{showReport?"إخفاء التقرير":"عرض التقرير"}</Btn>
          <Btn variant="outline" onClick={exportReportPDF} data-testid="export-pdf-btn"><FileDown size={14} className="inline ms-1"/>PDF</Btn>
          <Btn variant="outline" onClick={printReport} data-testid="print-btn"><Printer size={14} className="inline ms-1"/>طباعة</Btn>
        </>}
        <Btn variant="danger" onClick={deleteEval} data-testid="delete-eval-btn"><Trash2 size={14} className="inline ms-1"/>حذف</Btn>
      </div>
    </div>

    {showEdit && <EvalEditModal cid={cid} eid={eid} data={data} planGoals={planGoals} domainLabels={meta.domain_labels} domains={meta.domains} onClose={()=>setShowEdit(false)} onSaved={()=>{ setShowEdit(false); load(); }}/>}

    {/* PHASE GATE RAIL (Clinical 3-phase workflow v3.1) */}
    <PhaseGateRail data={data} onComparison={runComparison} comparing={comparing}/>

    {/* Side toggle for AI suggest & active panel */}
    <div className="flex items-center gap-2 text-sm" data-testid="side-toggle">
      <span className="text-[#4A5553]">الجانب النشط للإضافة الذكية:</span>
      <button onClick={()=>setSide("baseline")} data-testid="side-baseline" className={`px-3 py-1 rounded-full transition ${side==="baseline"?"bg-[#2F5233] text-white":"bg-[#F0EFEB] text-[#4A5553]"}`}>قبلي</button>
      <button onClick={()=>setSide("final")} data-testid="side-final" className={`px-3 py-1 rounded-full transition ${side==="final"?"bg-[#769FCD] text-white":"bg-[#F0EFEB] text-[#4A5553]"}`}>بعدي</button>
    </div>

    {/* Side-by-side */}
    <div className="grid lg:grid-cols-2 gap-5" data-testid="side-by-side">
      <VideoPanel side="baseline" label="المرحلة 1 — التقييم القبلي (Baseline)" colorClass="border-[#2F5233]/40" videoRef={baseRef} eid={eid} cid={cid} data={data} meta={meta} onChange={load}/>
      <VideoPanel side="final" label="المرحلة 2 — التقييم البعدي (Post)" colorClass="border-[#769FCD]/60" videoRef={finalRef} eid={eid} cid={cid} data={data} meta={meta} onChange={load}/>
    </div>

    {/* AI suggestions from text context */}
    <AISuggestPanel cid={cid} eid={eid} side={side} meta={meta} duration={data[side]?.duration_seconds || 60} onAdded={load}/>

    {/* Rubric */}
    <RubricPanel cid={cid} eid={eid} domain={data.domain} scores={data.rubric_scores || {}} onSaved={load}/>

    {/* Comparison report */}
    {showReport && data.comparison_summary && <ComparisonReport data={data} meta={meta}/>}
  </div>;
}

// ============= Video panel (one side) =============
function VideoPanel({ side, label, colorClass, videoRef, eid, cid, data, meta, onChange }) {
  const sideData = data[side] || { video_url:"", duration_seconds:0, notes:[] };
  const [urlInput, setUrlInput] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [addForm, setAddForm] = useState({ t:0, observation:"", tag:"", evidence_type:"" });
  const [aiBusy, setAiBusy] = useState(false);
  const [showRec, setShowRec] = useState(false);
  const [editingNote, setEditingNote] = useState(null); // note object being edited
  const [signing, setSigning] = useState(false);

  const isSigned = !!sideData.signed_off;
  const rubric = (data.rubric_scores || {})[side] || {};
  const rubricCount = Object.values(rubric).filter(v => typeof v === "number" && v > 0).length;

  const signOff = async () => {
    if (!window.confirm(side === "baseline"
      ? "توقيع المرحلة 1 (التقييم القبلي) سيقفل الفيديو والملاحظات لهذا الجانب ويفتح المرحلة 2. هل أنت متأكد؟"
      : "توقيع المرحلة 2 (التقييم البعدي) يُؤكّد اكتمال التقييم ويفتح المرحلة 3 (تقرير المقارنة الذكي). متابعة؟")) return;
    setSigning(true);
    try {
      await axios.post(`${API}/cases/${cid}/evaluations/${eid}/sign-off`, { side });
      onChange();
    } catch (err) { alert(fmtErr(err.response?.data?.detail)); } finally { setSigning(false); }
  };
  const unsign = async () => {
    if (!window.confirm("إلغاء توقيع هذه المرحلة لإعادة تعديلها؟")) return;
    setSigning(true);
    try {
      await axios.post(`${API}/cases/${cid}/evaluations/${eid}/sign-off`, { side, undo: true });
      onChange();
    } catch (err) { alert(fmtErr(err.response?.data?.detail)); } finally { setSigning(false); }
  };

  const videoSrc = absUrl(sideData.video_url);
  // YouTube / Vimeo handling
  const ytId = useMemo(() => {
    if (!sideData.video_url) return null;
    const m = sideData.video_url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/))([\w-]{11})/);
    return m ? m[1] : null;
  }, [sideData.video_url]);

  const setURL = async () => {
    if (!urlInput.trim()) return;
    setBusy(true);
    try {
      await axios.patch(`${API}/cases/${cid}/evaluations/${eid}/video`, { side, video_url: urlInput.trim(), duration_seconds: 0 });
      setUrlInput(""); onChange();
    } catch (err) { alert(fmtErr(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const uploadFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 200 * 1024 * 1024) { alert("الملف أكبر من 200MB"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    fd.append("side", side);
    fd.append("duration_seconds", "0");
    try {
      await axios.post(`${API}/cases/${cid}/evaluations/${eid}/upload-video`, fd, { headers:{ "Content-Type":"multipart/form-data" } });
      onChange();
    } catch (err) { alert(fmtErr(err.response?.data?.detail)); } finally { setBusy(false); e.target.value=""; }
  };
  const uploadRecordedBlob = async (blob, durationSec) => {
    if (!blob) return;
    if (blob.size > 200 * 1024 * 1024) { alert("التسجيل أكبر من 200MB"); return; }
    setBusy(true);
    const fd = new FormData();
    const filename = `recording_${Date.now()}.webm`;
    fd.append("file", new File([blob], filename, { type: blob.type || "video/webm" }));
    fd.append("side", side);
    fd.append("duration_seconds", String(Math.round(durationSec || 0)));
    try {
      await axios.post(`${API}/cases/${cid}/evaluations/${eid}/upload-video`, fd, { headers:{ "Content-Type":"multipart/form-data" } });
      setShowRec(false);
      onChange();
    } catch (err) { alert(fmtErr(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const onLoadedMeta = async () => {
    const v = videoRef.current;
    if (!v) return;
    const dur = Math.round(v.duration || 0);
    if (dur && dur !== sideData.duration_seconds) {
      try {
        await axios.patch(`${API}/cases/${cid}/evaluations/${eid}/video`, { side, video_url: sideData.video_url, duration_seconds: dur });
        onChange();
      } catch (err) {
        console.warn("Failed to auto-update video duration:", err);
      }
    }
  };
  const openAddAtCurrent = () => {
    const v = videoRef.current;
    const t = v ? Math.round(v.currentTime) : 0;
    setAddForm({ t, observation:"", tag:"", evidence_type:"" });
    setShowAdd(true);
  };
  const addNote = async (e) => {
    e.preventDefault();
    if (!addForm.tag) { alert("اختر الوسم"); return; }
    try {
      await axios.post(`${API}/cases/${cid}/evaluations/${eid}/notes`, {
        side, t: addForm.t, observation: addForm.observation,
        tag: addForm.tag, evidence_type: addForm.evidence_type,
      });
      setShowAdd(false); onChange();
    } catch (err) { alert(fmtErr(err.response?.data?.detail)); }
  };
  const delNote = async (nid) => {
    if (!window.confirm("حذف الملاحظة؟")) return;
    try { await axios.delete(`${API}/cases/${cid}/evaluations/${eid}/notes/${nid}?side=${side}`); onChange(); } catch(err) { alert(fmtErr(err.response?.data?.detail)); }
  };
  const saveNoteEdit = async () => {
    if (!editingNote) return;
    const body = { side, t: editingNote.t, observation: editingNote.observation, tag: editingNote.tag, evidence_type: editingNote.evidence_type || "" };
    try {
      await axios.patch(`${API}/cases/${cid}/evaluations/${eid}/notes/${editingNote.id}`, body);
      setEditingNote(null);
      onChange();
    } catch (err) { alert(fmtErr(err.response?.data?.detail)); }
  };
  const jumpTo = (t) => { const v = videoRef.current; if (v) { v.currentTime = t; v.play?.().catch(()=>{}); } };

  // AI vision extract from current frame
  const visionExtract = async () => {
    const v = videoRef.current;
    if (!v) { alert("لا يوجد فيديو محمّل"); return; }
    if (ytId) { alert("لا يمكن تحليل فيديو يوتيوب بصرياً. ارفع ملفاً أو ضع رابطاً مباشراً لـ mp4."); return; }
    setAiBusy(true);
    try {
      const canvas = document.createElement("canvas");
      canvas.width = v.videoWidth || 640;
      canvas.height = v.videoHeight || 360;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
      const b64 = canvas.toDataURL("image/jpeg", 0.7).split(",")[1];
      const t = Math.round(v.currentTime || 0);
      const { data: r } = await axios.post(`${API}/cases/${cid}/evaluations/${eid}/vision-extract`, { side, t, image_base64: b64 });
      if (!r.suggestions?.length) { alert("لم يكتشف الذكاء الاصطناعي أدلة جديدة في هذا الإطار."); return; }
      // auto-insert all suggestions as ai_suggested
      for (const s of r.suggestions) {
        await axios.post(`${API}/cases/${cid}/evaluations/${eid}/notes`, {
          side, t: s.t, observation: s.observation,
          tag: s.tag, evidence_type: s.evidence_type || "", ai_suggested: true,
        });
      }
      onChange();
    } catch (err) { alert(fmtErr(err.response?.data?.detail)); } finally { setAiBusy(false); }
  };

  const dur = sideData.duration_seconds || 0;
  const notesSorted = [...(sideData.notes || [])].sort((a,b) => a.t - b.t);

  return <Card className={`border-2 ${colorClass} relative ${isSigned?"bg-emerald-50/30":""}`} data-testid={`panel-${side}`}>
    <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
      <h3 className="font-semibold flex items-center gap-2 text-sm">
        {side==="baseline" ? <span className="w-2 h-2 rounded-full bg-[#2F5233]"/> : <span className="w-2 h-2 rounded-full bg-[#769FCD]"/>}
        {label}
        {isSigned && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 inline-flex items-center gap-1" data-testid={`signed-badge-${side}`}><Check size={10}/>موقّع</span>}
      </h3>
      <div className="flex items-center gap-2">
        <Pill color={side==="baseline"?"emerald":"blue"}>{notesSorted.length} ملاحظة</Pill>
        {!isSigned && sideData.video_url && notesSorted.length >= 1 && rubricCount >= 1 && <Btn variant="accent" onClick={signOff} disabled={signing} data-testid={`sign-off-${side}`}><Check size={12} className="inline ms-1"/>{signing?"...":"توقيع المرحلة"}</Btn>}
        {isSigned && <Btn variant="ghost" onClick={unsign} disabled={signing} data-testid={`unsign-${side}`}><RefreshCw size={12} className="inline ms-1"/>إلغاء التوقيع</Btn>}
      </div>
    </div>
    {isSigned && sideData.signed_off_by && <p className="text-[10px] text-emerald-700 -mt-1 mb-2">وُقّعت بواسطة <strong>{sideData.signed_off_by}</strong> · {sideData.signed_off_at ? new Date(sideData.signed_off_at).toLocaleString("ar-EG") : ""}</p>}
    {!isSigned && sideData.video_url && (notesSorted.length < 1 || rubricCount < 1) && <p className="text-[10px] text-[#A36F00] -mt-1 mb-2 px-2 py-1 bg-[#FFF7E6] rounded">للتوقيع: تحتاج {notesSorted.length<1?"ملاحظة واحدة على الأقل":""}{notesSorted.length<1 && rubricCount<1?" و":""}{rubricCount<1?"إكمال Rubric (نتيجة واحدة على الأقل)":""}</p>}

    {/* Video source controls */}
    {!sideData.video_url && <div className="space-y-2 mb-3">
      <div className="flex gap-2"><Input placeholder="رابط فيديو (YouTube / Vimeo / mp4)" value={urlInput} onChange={e=>setUrlInput(e.target.value)} data-testid={`url-input-${side}`}/>
        <Btn onClick={setURL} disabled={busy || !urlInput.trim()} data-testid={`url-save-${side}`}>ربط</Btn></div>
      <div className="text-center text-xs text-[#4A5553]">— أو —</div>
      <div className="flex flex-wrap gap-2 items-center">
        <label>
          <input type="file" accept="video/*" onChange={uploadFile} className="hidden" data-testid={`file-input-${side}`}/>
          <span className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[#E5E2DB] bg-white hover:bg-[#F0EFEB] cursor-pointer text-sm ${busy?"opacity-50":""}`}>
            <Upload size={14}/>{busy?"جاري الرفع...":"رفع ملف"}
          </span>
        </label>
        <Btn variant="outline" onClick={()=>setShowRec(true)} data-testid={`rec-open-${side}`}><Camera size={14} className="inline ms-1"/>تسجيل من الكاميرا</Btn>
        <span className="text-[10px] text-[#4A5553]">حتى 200MB</span>
      </div>
    </div>}

    {/* Player */}
    {sideData.video_url && (ytId ? (
      <div className="aspect-video bg-black rounded-lg overflow-hidden mb-2">
        <iframe src={`https://www.youtube.com/embed/${ytId}`} title={label} className="w-full h-full" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
      </div>
    ) : (
      <video ref={videoRef} src={videoSrc} controls className="w-full rounded-lg bg-black aspect-video mb-2" onLoadedMetadata={onLoadedMeta} crossOrigin="anonymous" data-testid={`video-${side}`}/>
    ))}

    {/* Controls (replace, change url, add note, AI vision) */}
    {sideData.video_url && <div className="flex gap-2 flex-wrap mb-3 text-xs">
      <Btn variant="outline" onClick={openAddAtCurrent} data-testid={`add-note-${side}`}><Plus size={12} className="inline ms-1"/>أضف ملاحظة عند الوقت الحالي</Btn>
      {!ytId && <Btn variant="outline" onClick={visionExtract} disabled={aiBusy} data-testid={`vision-${side}`}><Camera size={12} className="inline ms-1"/>{aiBusy?"جاري التحليل...":"تحليل الإطار بالذكاء البصري"}</Btn>}
      <Btn variant="ghost" onClick={async ()=>{ if(window.confirm("استبدال مصدر الفيديو؟ ستُحذف الملاحظات الحالية لهذا الجانب يدوياً عند الحاجة.")) {
        try { await axios.patch(`${API}/cases/${cid}/evaluations/${eid}/video`, { side, video_url:"", duration_seconds:0 }); onChange(); } catch(err){alert(fmtErr(err.response?.data?.detail));}
      }}}><RefreshCw size={12} className="inline ms-1"/>تغيير المصدر</Btn>
      {dur > 0 && <span className="text-[#4A5553] self-center">المدة: {fmtTime(dur)}</span>}
    </div>}

    {/* Timeline */}
    {sideData.video_url && dur > 0 && <Timeline duration={dur} notes={notesSorted} onJump={jumpTo} tagLabels={meta.tag_labels} side={side}/>}

    {/* Add note form */}
    {showAdd && <form onSubmit={addNote} className="mt-3 p-3 bg-[#F9F8F6] rounded-lg space-y-2" data-testid={`note-form-${side}`}>
      <div className="grid grid-cols-3 gap-2">
        <Input type="number" min={0} placeholder="الوقت (ث)" value={addForm.t} onChange={e=>setAddForm({...addForm, t: parseFloat(e.target.value)||0})} data-testid={`nf-t-${side}`}/>
        <Select required value={addForm.tag} onChange={e=>setAddForm({...addForm, tag:e.target.value})} data-testid={`nf-tag-${side}`}>
          <option value="">الوسم *</option>
          {meta.tags.map(t => <option key={t} value={t}>{meta.tag_labels[t] || t}</option>)}
        </Select>
        <Select value={addForm.evidence_type} onChange={e=>setAddForm({...addForm, evidence_type:e.target.value})} data-testid={`nf-evt-${side}`}>
          <option value="">نوع الدليل (اختياري)</option>
          {meta.evidence_types.map(t => <option key={t} value={t}>{meta.evidence_labels[t] || t}</option>)}
        </Select>
      </div>
      <textarea required value={addForm.observation} onChange={e=>setAddForm({...addForm, observation:e.target.value})} placeholder="الملاحظة الموثّقة بالفيديو *" className="w-full px-3 py-2 rounded-lg bg-white border border-[#E5E2DB] focus:ring-2 focus:ring-[#2F5233]/30 outline-none min-h-[60px]" data-testid={`nf-obs-${side}`}/>
      <div className="flex gap-2"><Btn type="submit" data-testid={`nf-save-${side}`}><Save size={12} className="inline ms-1"/>حفظ</Btn>
        <Btn variant="outline" type="button" onClick={()=>setShowAdd(false)}>إلغاء</Btn></div>
    </form>}

    {/* Notes list */}
    {notesSorted.length > 0 && <div className="mt-3 space-y-2">
      <h4 className="text-xs font-semibold text-[#4A5553]">الأدلة على الخط الزمني ({notesSorted.length})</h4>
      <div className="space-y-1.5 max-h-72 overflow-y-auto pe-1">{notesSorted.map(n => editingNote?.id === n.id ? (
        <div key={n.id} className="p-2 bg-amber-50 border border-amber-200 rounded text-sm space-y-2" data-testid={`note-edit-${side}-${n.id}`}>
          <div className="grid grid-cols-3 gap-2">
            <Input type="number" min={0} value={editingNote.t} onChange={e=>setEditingNote({...editingNote, t: parseFloat(e.target.value)||0})} data-testid={`ne-t-${side}-${n.id}`}/>
            <Select required value={editingNote.tag} onChange={e=>setEditingNote({...editingNote, tag:e.target.value})} data-testid={`ne-tag-${side}-${n.id}`}>
              {meta.tags.map(t => <option key={t} value={t}>{meta.tag_labels[t] || t}</option>)}
            </Select>
            <Select value={editingNote.evidence_type || ""} onChange={e=>setEditingNote({...editingNote, evidence_type:e.target.value})} data-testid={`ne-evt-${side}-${n.id}`}>
              <option value="">— نوع الدليل —</option>
              {meta.evidence_types.map(t => <option key={t} value={t}>{meta.evidence_labels[t] || t}</option>)}
            </Select>
          </div>
          <textarea value={editingNote.observation} onChange={e=>setEditingNote({...editingNote, observation:e.target.value})} className="w-full px-2 py-1.5 rounded bg-white border border-[#E5E2DB] focus:ring-2 focus:ring-[#2F5233]/30 outline-none min-h-[48px] text-xs" data-testid={`ne-obs-${side}-${n.id}`}/>
          <div className="flex gap-2">
            <Btn onClick={saveNoteEdit} data-testid={`ne-save-${side}-${n.id}`}><Save size={12} className="inline ms-1"/>حفظ التعديل</Btn>
            <Btn variant="outline" onClick={()=>setEditingNote(null)}>إلغاء</Btn>
          </div>
        </div>
      ) : (
        <div key={n.id} className="p-2 bg-[#F9F8F6] rounded text-sm flex justify-between gap-2" data-testid={`note-${side}-${n.id}`}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <button onClick={()=>jumpTo(n.t)} className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-[#2F5233]/10 text-[#2F5233] hover:bg-[#2F5233]/20" title="انتقل لهذه اللحظة">{fmtTime(n.t)}</button>
              <Pill color={side==="baseline"?"emerald":"blue"}>{meta.tag_labels[n.tag] || n.tag}</Pill>
              {n.evidence_type && <Pill color="slate">{meta.evidence_labels[n.evidence_type] || n.evidence_type}</Pill>}
              {n.ai_suggested && <Pill color="purple"><Sparkles size={10} className="inline ms-0.5"/>AI</Pill>}
            </div>
            <p className="text-xs mt-1 text-[#1A201E]">{n.observation}</p>
            {n.author && <p className="text-[10px] text-[#4A5553] mt-0.5">{n.author}</p>}
          </div>
          <div className="flex flex-col gap-1">
            <button onClick={()=>setEditingNote({...n})} className="p-1 hover:bg-amber-50 rounded text-amber-700" title="تعديل" data-testid={`edit-note-${side}-${n.id}`}><Pencil size={12}/></button>
            <button onClick={()=>delNote(n.id)} className="p-1 hover:bg-red-50 rounded text-[#D9534F]" title="حذف" data-testid={`del-note-${side}-${n.id}`}><Trash2 size={12}/></button>
          </div>
        </div>
      ))}</div>
    </div>}

    {showRec && <CameraRecorder side={side} onCancel={()=>setShowRec(false)} onSave={uploadRecordedBlob} busy={busy}/>}
  </Card>;
}

// ============= Live Camera Recorder Modal =============
function CameraRecorder({ side, onCancel, onSave, busy }) {
  const liveRef = useRef(null);
  const previewRef = useRef(null);
  const mediaRecRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const startedAtRef = useRef(0);
  const tickRef = useRef(null);

  const [status, setStatus] = useState("idle"); // idle | live | recording | recorded | error
  const [errorMsg, setErrorMsg] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const [withAudio, setWithAudio] = useState(false);
  const [blob, setBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");

  const cleanup = useCallback(() => {
    try {
      if (mediaRecRef.current?.state === "recording") mediaRecRef.current.stop();
    } catch (err) {
      console.warn("Recorder cleanup stop failed:", err);
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
  }, []);

  useEffect(() => () => {
    cleanup();
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [cleanup, previewUrl]);

  const startCamera = async () => {
    setErrorMsg(""); setBlob(null);
    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(""); }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: "user" },
        audio: withAudio,
      });
      streamRef.current = stream;
      if (liveRef.current) { liveRef.current.srcObject = stream; await liveRef.current.play().catch(()=>{}); }
      setStatus("live");
    } catch (err) {
      console.error("Camera access denied:", err);
      setErrorMsg("تعذّر الوصول للكاميرا — تأكد من السماح بالأذونات. " + (err?.message || ""));
      setStatus("error");
    }
  };

  const startRecording = () => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    let mimeType = "video/webm;codecs=vp9,opus";
    if (!window.MediaRecorder?.isTypeSupported(mimeType)) mimeType = "video/webm;codecs=vp8,opus";
    if (!window.MediaRecorder?.isTypeSupported(mimeType)) mimeType = "video/webm";
    try {
      const rec = new MediaRecorder(streamRef.current, { mimeType });
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const b = new Blob(chunksRef.current, { type: mimeType });
        setBlob(b);
        const url = URL.createObjectURL(b);
        setPreviewUrl(url);
        // stop camera tracks once we have the recording
        streamRef.current?.getTracks().forEach(t => t.stop());
        streamRef.current = null;
        setStatus("recorded");
      };
      mediaRecRef.current = rec;
      rec.start(1000); // 1s chunks
      startedAtRef.current = Date.now();
      setElapsed(0);
      tickRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000));
      }, 250);
      setStatus("recording");
    } catch (err) {
      console.error("MediaRecorder init failed:", err);
      setErrorMsg("متصفحك لا يدعم تسجيل الفيديو في هذه الصيغة.");
      setStatus("error");
    }
  };

  const stopRecording = () => {
    try { mediaRecRef.current?.stop(); } catch (err) { console.warn("Stop failed:", err); }
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
  };

  const discard = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(""); setBlob(null); setElapsed(0); setStatus("idle");
  };

  const sizeKB = blob ? Math.round(blob.size / 1024) : 0;

  return <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid={`recorder-modal-${side}`} dir="rtl">
    <div className="bg-white rounded-xl border border-[#E5E2DB] w-full max-w-2xl max-h-[90vh] overflow-y-auto">
      <div className="flex justify-between items-center p-4 border-b border-[#E5E2DB]">
        <h3 className="text-lg font-semibold font-heading flex items-center gap-2">
          <Camera size={18}/>تسجيل مباشر — {side==="baseline"?"قبل التدخل":"بعد التدخل"}
        </h3>
        <button onClick={()=>{ cleanup(); onCancel(); }} className="p-1 hover:bg-[#F0EFEB] rounded" data-testid={`rec-close-${side}`}><X size={18}/></button>
      </div>

      <div className="p-4 space-y-3">
        {errorMsg && <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg text-sm text-rose-800 flex items-start gap-2">
          <AlertTriangle size={14} className="mt-0.5 flex-shrink-0"/>{errorMsg}
        </div>}

        {status === "idle" && <div className="space-y-3 text-center py-6">
          <p className="text-sm text-[#4A5553]">سجّل فيديو مباشر للطفل من كاميرا جهازك. تأكّد من موافقة ولي الأمر وخصوصية المكان.</p>
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={withAudio} onChange={e=>setWithAudio(e.target.checked)} className="rounded"/>
            <Mic size={14}/>تضمين الصوت
          </label>
          <div><Btn onClick={startCamera} data-testid={`rec-start-cam-${side}`}><Camera size={14} className="inline ms-1"/>فتح الكاميرا</Btn></div>
        </div>}

        {(status === "live" || status === "recording") && <div className="space-y-3">
          <div className="relative">
            <video ref={liveRef} muted playsInline className="w-full aspect-video bg-black rounded-lg" data-testid={`rec-live-${side}`}/>
            {status === "recording" && <div className="absolute top-2 left-2 flex items-center gap-2 px-2.5 py-1 rounded-full bg-rose-600 text-white text-xs font-mono">
              <Circle size={10} className="fill-white animate-pulse"/>تسجيل {fmtTime(elapsed)}
            </div>}
          </div>
          <div className="flex justify-center gap-2">
            {status === "live" && <Btn variant="danger" onClick={startRecording} data-testid={`rec-go-${side}`}><Circle size={12} className="inline ms-1 fill-white"/>بدء التسجيل</Btn>}
            {status === "recording" && <Btn variant="primary" onClick={stopRecording} data-testid={`rec-stop-${side}`}><Square size={12} className="inline ms-1"/>إيقاف</Btn>}
            <Btn variant="outline" onClick={()=>{ cleanup(); onCancel(); }}>إلغاء</Btn>
          </div>
        </div>}

        {status === "recorded" && <div className="space-y-3">
          <video ref={previewRef} src={previewUrl} controls className="w-full aspect-video bg-black rounded-lg" data-testid={`rec-preview-${side}`}/>
          <div className="flex justify-between items-center text-xs text-[#4A5553]">
            <span>المدة: {fmtTime(elapsed)} · الحجم: {sizeKB} KB</span>
          </div>
          <div className="flex justify-center gap-2 flex-wrap">
            <Btn onClick={()=>onSave(blob, elapsed)} disabled={busy} data-testid={`rec-save-${side}`}><Save size={14} className="inline ms-1"/>{busy?"جاري الحفظ...":"حفظ التسجيل"}</Btn>
            <Btn variant="outline" onClick={discard} disabled={busy} data-testid={`rec-discard-${side}`}><RefreshCw size={14} className="inline ms-1"/>إعادة التسجيل</Btn>
          </div>
        </div>}
      </div>
    </div>
  </div>;
}

// ============= Edit Evaluation Modal =============
function EvalEditModal({ cid, eid, data, planGoals, domainLabels, domains, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: data.title || "",
    domain: data.domain || "",
    goal_id: data.goal_id || "",
  });
  const [busy, setBusy] = useState(false);
  const filtered = form.domain ? planGoals.filter(g => g.domain === form.domain) : planGoals;

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // Always send all 3 fields; goal_id="" clears the link
      await axios.patch(`${API}/cases/${cid}/evaluations/${eid}`, {
        title: form.title, domain: form.domain, goal_id: form.goal_id || "",
      });
      onSaved();
    } catch (err) { alert(fmtErr(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  return <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" dir="rtl" data-testid="eval-edit-modal">
    <div className="bg-white rounded-xl border border-[#E5E2DB] w-full max-w-lg">
      <div className="flex justify-between items-center p-4 border-b border-[#E5E2DB]">
        <h3 className="text-lg font-semibold font-heading flex items-center gap-2"><Pencil size={16}/>تعديل التقييم</h3>
        <button onClick={onClose} className="p-1 hover:bg-[#F0EFEB] rounded"><X size={16}/></button>
      </div>
      <form onSubmit={save} className="p-4 space-y-3">
        <div>
          <label className="text-xs text-[#4A5553] block mb-1">عنوان التقييم</label>
          <Input required value={form.title} onChange={e=>setForm({...form, title:e.target.value})} data-testid="edit-title"/>
        </div>
        <div>
          <label className="text-xs text-[#4A5553] block mb-1">المجال</label>
          <Select required value={form.domain} onChange={e=>setForm({...form, domain:e.target.value, goal_id:""})} data-testid="edit-domain">
            {domains.map(d => <option key={d} value={d}>{domainLabels[d] || d}</option>)}
          </Select>
        </div>
        <div>
          <label className="text-xs text-[#4A5553] block mb-1">الهدف المرتبط من خطة التدخل (اختياري)</label>
          <Select value={form.goal_id} onChange={e=>setForm({...form, goal_id:e.target.value})} data-testid="edit-goal">
            <option value="">— بدون ربط —</option>
            {filtered.map(g => <option key={g.id} value={g.id}>{g.title}{g.responsible?` (${g.responsible})`:""}</option>)}
          </Select>
          {filtered.length === 0 && form.domain && <p className="text-xs text-amber-700 mt-1">لا توجد أهداف في هذا المجال ضمن خطة الحالة. يمكنك إضافتها من تبويب "خطة التدخل".</p>}
        </div>
        <div className="flex gap-2 pt-2">
          <Btn type="submit" disabled={busy} data-testid="edit-save"><Save size={14} className="inline ms-1"/>{busy?"...":"حفظ التعديلات"}</Btn>
          <Btn variant="outline" type="button" onClick={onClose}>إلغاء</Btn>
        </div>
      </form>
    </div>
  </div>;
}

// ============= Timeline (visual ruler with markers) =============
function Timeline({ duration, notes, onJump, tagLabels, side }) {
  return <div className="relative h-10 bg-[#F0EFEB] rounded-lg border border-[#E5E2DB] mb-2 group" data-testid={`timeline-${side}`}>
    {/* Tick marks every ~20% */}
    {[0, 0.25, 0.5, 0.75, 1].map((p) => <div key={`tick-${p}`} className="absolute top-0 bottom-0 border-s border-[#E5E2DB] opacity-60" style={{ insetInlineStart: `${p*100}%` }}>
      <span className="absolute -bottom-4 -translate-x-1/2 text-[9px] text-[#4A5553]">{fmtTime(duration*p)}</span>
    </div>)}
    {notes.map(n => {
      const pct = Math.min(100, Math.max(0, (n.t / Math.max(1, duration)) * 100));
      return <button key={n.id} onClick={()=>onJump(n.t)}
        title={`${fmtTime(n.t)} — ${tagLabels[n.tag] || n.tag}: ${n.observation}`}
        className={`absolute top-1 bottom-1 -translate-x-1/2 w-1.5 rounded-full hover:w-2.5 transition-all ${side==="baseline"?"bg-[#2F5233]":"bg-[#769FCD]"} ${n.ai_suggested?"ring-2 ring-purple-300":""}`}
        style={{ insetInlineStart: `${pct}%` }}
        data-testid={`marker-${side}-${n.id}`}
      />;
    })}
  </div>;
}

// ============= AI Suggest Panel (text-based) =============
function AISuggestPanel({ cid, eid, side, meta, duration, onAdded }) {
  const [context, setContext] = useState("");
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  const suggest = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/cases/${cid}/evaluations/${eid}/suggest`, { side, context });
      // Attach stable client-side ids so React keys remain stable across re-renders
      const withIds = (data.suggestions || []).map((s, i) => ({ ...s, _uid: `${Date.now()}-${i}-${Math.random().toString(36).slice(2,7)}` }));
      setSuggestions(withIds);
    } catch (err) { alert(fmtErr(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const adopt = async (s) => {
    try {
      await axios.post(`${API}/cases/${cid}/evaluations/${eid}/notes`, {
        side, t: s.t, observation: s.observation,
        tag: s.tag, evidence_type: s.evidence_type || "", ai_suggested: true,
      });
      setSuggestions(prev => prev.filter(x => x !== s));
      onAdded();
    } catch (err) { alert(fmtErr(err.response?.data?.detail)); }
  };
  const adoptAll = async () => {
    const failed = [];
    for (const s of suggestions) {
      try {
        await axios.post(`${API}/cases/${cid}/evaluations/${eid}/notes`, {
          side, t: s.t, observation: s.observation, tag: s.tag,
          evidence_type: s.evidence_type || "", ai_suggested: true,
        });
      } catch (err) {
        console.warn("Failed to adopt suggestion:", s, err);
        failed.push(s);
      }
    }
    if (failed.length) alert(`تعذّر اعتماد ${failed.length} اقتراحاً.`);
    setSuggestions([]); onAdded();
  };

  return <Card data-testid="ai-suggest-panel">
    <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
      <h3 className="text-lg font-semibold font-heading flex items-center gap-2"><Wand2 size={18}/>اقتراحات الذكاء الاصطناعي ({side==="baseline"?"قبلي":"بعدي"})</h3>
      <Btn onClick={suggest} disabled={busy} data-testid="ai-suggest-btn"><Sparkles size={14} className="inline ms-1"/>{busy?"يفكّر...":"اقترح نقاط أدلة"}</Btn>
    </div>
    <p className="text-xs text-[#4A5553] mb-2">صف باختصار ما يحدث في الفيديو (سياق المهمة، الأسلوب المستخدم، نوع الدعم) ليولّد الذكاء نقاطاً قابلة للملاحظة. يمكنك أيضاً استخدام زر "تحليل الإطار" أعلى الفيديو لاستخراج أدلة بصرية مباشرة.</p>
    <textarea value={context} onChange={e=>setContext(e.target.value)} placeholder={`مثال: في فيديو ${side==="baseline"?"قبلي":"بعدي"} مدته ${duration}ث، الطفل في موقف طلب مساعدة من المعلمة مع تلقين لفظي.`} className="w-full px-3 py-2 rounded-lg bg-white border border-[#E5E2DB] focus:ring-2 focus:ring-[#2F5233]/30 outline-none min-h-[60px]" data-testid="ai-context"/>
    {suggestions.length > 0 && <div className="mt-3 space-y-2">
      <div className="flex justify-between items-center"><span className="text-sm font-semibold">اقتراحات جاهزة ({suggestions.length})</span>
        <Btn variant="outline" onClick={adoptAll} data-testid="adopt-all-btn"><Check size={12} className="inline ms-1"/>اعتمد الكل</Btn></div>
      {suggestions.map((s, i) => <div key={s._uid || `sug-${i}`} className="p-2 bg-purple-50 rounded border border-purple-200 flex justify-between gap-2" data-testid={`sug-${i}`}>
        <div className="flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-white text-[#2F5233]">{fmtTime(s.t)}</span>
            <Pill color="purple">{meta.tag_labels[s.tag] || s.tag}</Pill>
            {s.evidence_type && <Pill color="slate">{meta.evidence_labels[s.evidence_type] || s.evidence_type}</Pill>}
          </div>
          <p className="text-xs mt-1">{s.observation}</p>
        </div>
        <button onClick={()=>adopt(s)} className="p-2 hover:bg-emerald-100 rounded text-emerald-700 self-start" title="اعتمد هذا الاقتراح" data-testid={`adopt-${i}`}><Check size={14}/></button>
      </div>)}
    </div>}
  </Card>;
}

// ============= Rubric Panel =============
const RUBRIC_INDICATORS = {
  communication: ["مبادرة التواصل", "وضوح النطق/الإشارة", "الاستجابة لطلبات الكبار", "استخدام بدائل التواصل"],
  behavior: ["تنظيم الانفعال", "تقبّل التغيير", "الالتزام بالحدود", "الاستقلالية"],
  academic: ["الانتباه والتركيز", "اتباع التعليمات", "إتمام المهمة", "نقل المهارة"],
  motor: ["التآزر البصري الحركي", "المهارات الدقيقة", "المهارات الكبرى", "الجرأة الحركية"],
  sensory_regulation: ["تنظيم الحسي البصري", "تنظيم الحسي السمعي", "تنظيم الحسي اللمسي", "التحمّل الزمني"],
  social_participation: ["التواصل البصري", "المشاركة مع الأقران", "اللعب المشترك", "التناوب"],
};

function RubricPanel({ cid, eid, domain, scores, onSaved }) {
  const indicators = RUBRIC_INDICATORS[domain] || [];
  const [local, setLocal] = useState(scores);
  const [busy, setBusy] = useState(false);
  useEffect(() => setLocal(scores), [scores]);

  const set = (ind, sideKey, val) => {
    setLocal(prev => ({ ...prev, [ind]: { ...(prev[ind]||{}), [sideKey]: val } }));
  };
  const save = async () => {
    setBusy(true);
    try { await axios.put(`${API}/cases/${cid}/evaluations/${eid}/rubric`, { scores: local }); onSaved(); }
    catch (err) { alert(fmtErr(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const totals = useMemo(() => {
    let b=0, f=0;
    indicators.forEach(ind => { b += local[ind]?.baseline || 0; f += local[ind]?.final || 0; });
    return { b, f, max: indicators.length * 5 };
  }, [local, indicators]);

  return <Card data-testid="rubric-panel">
    <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
      <h3 className="text-lg font-semibold font-heading flex items-center gap-2"><BarChart3 size={18}/>درجات المقياس (Rubric)</h3>
      <div className="flex items-center gap-3 text-sm">
        <span className="text-[#4A5553]">قبلي: <strong className="text-[#2F5233]">{totals.b}</strong>/{totals.max}</span>
        <span className="text-[#4A5553]">بعدي: <strong className="text-[#527598]">{totals.f}</strong>/{totals.max}</span>
        <Btn onClick={save} disabled={busy} data-testid="save-rubric"><Save size={14} className="inline ms-1"/>{busy?"...":"حفظ الدرجات"}</Btn>
      </div>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-xs text-[#4A5553] text-right">
          <th className="py-2">المؤشر</th>
          <th className="py-2 text-center">قبلي (1-5)</th>
          <th className="py-2 text-center">بعدي (1-5)</th>
          <th className="py-2 text-center">الفرق</th>
        </tr></thead>
        <tbody>{indicators.map(ind => {
          const b = local[ind]?.baseline || 0;
          const f = local[ind]?.final || 0;
          const diff = f - b;
          const diffCls = diff > 0
            ? "bg-emerald-100 text-emerald-700"
            : diff < 0
              ? "bg-rose-100 text-rose-700"
              : "bg-[#F0EFEB] text-[#4A5553]";
          return <tr key={ind} className="border-t border-[#E5E2DB]" data-testid={`rub-row-${ind}`}>
            <td className="py-2">{ind}</td>
            <td className="py-2"><ScoreCells value={b} onChange={v=>set(ind, "baseline", v)} testidPrefix={`rub-b-${ind}`}/></td>
            <td className="py-2"><ScoreCells value={f} onChange={v=>set(ind, "final", v)} testidPrefix={`rub-f-${ind}`}/></td>
            <td className="py-2 text-center"><span className={`text-xs font-mono px-2 py-0.5 rounded ${diffCls}`}>{diff>0?`+${diff}`:diff}</span></td>
          </tr>;
        })}</tbody>
      </table>
    </div>
  </Card>;
}

function ScoreCells({ value, onChange, testidPrefix }) {
  return <div className="flex gap-1 justify-center">{[1,2,3,4,5].map(n => <button key={n} onClick={()=>onChange(n===value?0:n)} type="button"
    className={`w-7 h-7 rounded text-xs font-mono transition ${value>=n?"bg-[#2F5233] text-white":"bg-[#F0EFEB] text-[#4A5553] hover:bg-[#E5E2DB]"}`}
    data-testid={`${testidPrefix}-${n}`}>{n}</button>)}</div>;
}

// ============= Comparison Report =============
function ComparisonReport({ data, meta }) {
  const c = data.comparison_summary || {};
  const baseCount = data.baseline?.notes?.length || 0;
  const finalCount = data.final?.notes?.length || 0;

  // tag breakdown
  const tagCounts = useMemo(() => {
    const map = {};
    (data.baseline?.notes || []).forEach(n => {
      map[n.tag] = map[n.tag] || { b:0, f:0 };
      map[n.tag].b++;
    });
    (data.final?.notes || []).forEach(n => {
      map[n.tag] = map[n.tag] || { b:0, f:0 };
      map[n.tag].f++;
    });
    return map;
  }, [data]);

  return <Card data-testid="comparison-report">
    <div id="eval-report-printable" className="p-4 bg-white" dir="rtl">
      <div className="text-center mb-5 pb-3 border-b-2 border-[#2F5233]">
        <h2 className="text-2xl font-bold font-heading">تقرير المقارنة قبل/بعد</h2>
        <p className="text-sm text-[#4A5553] mt-1">{data.title} — {meta.domain_labels[data.domain] || data.domain}</p>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-5">
        <div className="p-3 bg-[#2F5233]/5 rounded-lg border border-[#2F5233]/30">
          <p className="text-xs text-[#4A5553]">عدد الأدلة - قبلي</p>
          <p className="text-2xl font-bold text-[#2F5233]">{baseCount}</p>
        </div>
        <div className="p-3 bg-[#769FCD]/10 rounded-lg border border-[#769FCD]/30">
          <p className="text-xs text-[#4A5553]">عدد الأدلة - بعدي</p>
          <p className="text-2xl font-bold text-[#527598]">{finalCount}</p>
        </div>
      </div>

      {c.overall_interpretation && <div className="mb-4 p-3 bg-[#F9F8F6] rounded-lg">
        <h4 className="text-sm font-semibold mb-1">التفسير العام</h4>
        <p className="text-sm">{c.overall_interpretation}</p>
      </div>}

      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <Section title="لحظات بارزة - قبلي" items={c.notable_baseline_moments || []} color="border-[#2F5233]/30"/>
        <Section title="لحظات بارزة - بعدي" items={c.notable_final_moments || []} color="border-[#769FCD]/30"/>
        <Section title="أبرز التحسينات" items={c.strongest_improvements || []} color="border-emerald-300"/>
        <Section title="تحديات متبقية" items={c.remaining_challenges || []} color="border-amber-300"/>
      </div>

      {Object.keys(tagCounts).length > 0 && <div className="mb-4">
        <h4 className="text-sm font-semibold mb-2">توزيع الأدلة بحسب الوسوم</h4>
        <div className="space-y-1.5">{Object.entries(tagCounts).map(([tag, v]) => {
          const max = Math.max(v.b, v.f, 1);
          return <div key={tag} className="text-xs">
            <div className="flex justify-between mb-0.5"><span>{meta.tag_labels[tag] || tag}</span><span className="font-mono">قبلي {v.b} · بعدي {v.f}</span></div>
            <div className="flex gap-1 h-3">
              <div className="bg-[#2F5233] rounded-s" style={{ width: `${(v.b/max)*48}%` }}/>
              <div className="bg-[#769FCD] rounded-e" style={{ width: `${(v.f/max)*48}%` }}/>
            </div>
          </div>;
        })}</div>
      </div>}

      {c.tag_shifts?.length > 0 && <div className="mb-4">
        <h4 className="text-sm font-semibold mb-2">تحوّلات الوسوم (تفسير AI)</h4>
        <div className="space-y-1">{c.tag_shifts.map((s) => <div key={`shift-${s.tag}`} className="text-xs p-2 bg-[#F9F8F6] rounded">
          <strong>{meta.tag_labels[s.tag] || s.tag}</strong>: قبلي {s.baseline_count} → بعدي {s.final_count} — {s.interpretation}
        </div>)}</div>
      </div>}

      {c.professional_caveat && <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
        <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5"/>
        <p className="text-xs text-amber-900">{c.professional_caveat}</p>
      </div>}

      <p className="text-[10px] text-[#4A5553] mt-4 text-center">تقرير ملاحظي مبني على أدلة فيديو موثّقة — ليس تشخيصاً طبياً.</p>
    </div>
  </Card>;
}

function Section({ title, items, color }) {
  return <div className={`p-3 rounded-lg border ${color} bg-white`}>
    <h4 className="text-sm font-semibold mb-2">{title}</h4>
    {items.length === 0 ? <p className="text-xs text-[#4A5553]">—</p> :
      <ul className="text-xs space-y-1 list-disc ps-4">{items.map((it, i) => <li key={`${typeof it === "string" ? it.slice(0,40) : i}-${i}`}>{it}</li>)}</ul>}
  </div>;
}
